import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChartLineUp, ArrowRight, Check, Warning, FileMinus, Browsers,
  Target, Broadcast, Quotes, CaretDown, ShieldCheck, GoogleLogo, FacebookLogo,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const AGITATION_ITEMS = [
  { icon: Warning, title: "The 2 AM Panic", desc: "Waking up to realize a Meta campaign burned $500 at an $80 CPL because you fell asleep." },
  { icon: FileMinus, title: "Reporting Hell", desc: "Losing your entire Sunday copying and pasting metrics into Google Slides for Monday client calls." },
  { icon: Browsers, title: "Tab Fatigue", desc: "Jumping between 14 Google Ads accounts and 8 Meta Business Managers just to check daily spend." },
];

const MECHANISM_ITEMS = [
  { icon: Target, title: "The Kill Switch", desc: "CPL hits your max? The ad is paused instantly. No manual checking required." },
  { icon: ChartLineUp, title: "The Scale Engine", desc: "ROAS over 3.0? Budget increases by 20% automatically to ride the winning wave." },
  { icon: Broadcast, title: "Live Command", desc: "WebSockets stream data live across all client accounts. Zero refreshing needed." },
];

const TESTIMONIALS = [
  { quote: "AdCrew let me take on 4 more clients without working weekends. The automated rules pay for the software on day one.", author: "David C.", role: "Freelance Media Buyer", img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODM0MjI2MzR8MA&ixlib=rb-4.1.0&q=85" },
  { quote: "I fired my white-label agency and just use AdCrew now. The client PDF reports look like I spent hours on them.", author: "Sarah M.", role: "E-com Growth Partner", img: "https://images.unsplash.com/photo-1580489944761-15a19d654956?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODM0MjI2MzR8MA&ixlib=rb-4.1.0&q=85" },
  { quote: "The instant push notification when my CPL spiked saved me $800 yesterday alone.", author: "James T.", role: "Lead Gen Agency", img: "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODM0MjI2MzR8MA&ixlib=rb-4.1.0&q=85" },
];

const TIER_FEATURES = {
  solo: ["1 account per platform", "Real-time dashboard", "PDF reports", "Autonomous campaign rules", "Email + SMS alerts"],
  agency: ["Up to 5 accounts per platform", "Everything in Solo", "White-label branded reports", "Logo upload", "Priority automation rules"],
};

const FAQ_ITEMS = [
  { q: "Will my clients know I use this?", a: "No. The Agency tier provides 100% white-labeled PDF reports with your own logo." },
  { q: "Do I have to sign a yearly contract?", a: "Never. It's a month-to-month subscription. Cancel instantly via our self-serve Stripe customer portal." },
  { q: "Is it hard to set up?", a: "You can connect your Google and Meta accounts in 2 clicks. The onboarding wizard sets up your first rules in under 60 seconds." },
  { q: "What if a rule goes rogue?", a: "You're always in control. You receive instant push notifications and alerts before any major budget shift executes." },
];

const YELLOW = "#FACC15";

function SectionOverline({ children }) {
  return (
    <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: YELLOW }}>
      {children}
    </span>
  );
}

function FaqRow({ item, index, openIndex, setOpenIndex }) {
  const isOpen = openIndex === index;
  return (
    <div className="border border-white/10 hover:border-white/25 transition-colors duration-300">
      <button
        type="button"
        onClick={() => setOpenIndex(isOpen ? null : index)}
        className="w-full flex items-center justify-between gap-4 p-6 text-left"
        data-testid={`faq-trigger-${index}`}
      >
        <span className="font-landing-heading font-semibold text-base sm:text-lg text-white">{item.q}</span>
        <CaretDown
          className={`h-5 w-5 flex-shrink-0 text-white/50 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className="overflow-hidden transition-all duration-300 ease-out"
        style={{ maxHeight: isOpen ? "200px" : "0px" }}
        data-testid={`faq-content-${index}`}
      >
        <p className="px-6 pb-6 text-sm text-white/60 leading-relaxed font-landing-body">{item.a}</p>
      </div>
    </div>
  );
}

export default function Landing() {
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [tiers, setTiers] = useState({});
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    api.get("/billing/tiers").then((res) => setTiers(res.data)).catch(() => {});
  }, []);

  const scrollToMechanism = () => {
    document.getElementById("mechanism-section")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen text-white font-landing-body" style={{ backgroundColor: "#050816" }}>
      {/* NAV */}
      <nav className="fixed top-0 inset-x-0 z-50 border-b border-white/10 bg-[#050816]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 flex items-center justify-center" style={{ backgroundColor: `${YELLOW}25` }}>
              <ChartLineUp className="h-4.5 w-4.5" weight="bold" style={{ color: YELLOW }} />
            </div>
            <span className="font-landing-heading font-black text-lg tracking-tight">AdCrew OS</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:bg-white/10 rounded-none" data-testid="nav-login-button">
                Log In
              </Button>
            </Link>
            <Link to="/login">
              <Button
                className="rounded-none font-bold uppercase tracking-wide text-black hover:-translate-y-0.5 transition-transform duration-300"
                style={{ backgroundColor: YELLOW }}
                data-testid="nav-get-started-button"
              >
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section
        className="relative pt-40 pb-28 px-6 overflow-hidden"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(5,8,22,0.85), rgba(5,8,22,0.97)), url('https://images.pexels.com/photos/6485524/pexels-photo-6485524.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <SectionOverline>Built for 1-Person Ad Agencies</SectionOverline>
            <h1 className="font-landing-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[1.05] mt-5">
              Run $1M+ in Ad Spend.<br />
              <span style={{ color: YELLOW }}>Never Hire a Media Buyer.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">
              The autonomous command center for solo-agencies. Auto-pause losing ads, scale high ROAS campaigns,
              and generate white-label client reports while you sleep.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/login">
                <Button
                  size="lg"
                  className="gap-2 rounded-none font-bold uppercase tracking-wide text-black px-8 h-12 hover:-translate-y-1 transition-all duration-300"
                  style={{ backgroundColor: YELLOW }}
                  data-testid="hero-primary-cta-button"
                >
                  Start 14-Day Free Trial <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Button
                size="lg"
                variant="outline"
                onClick={scrollToMechanism}
                className="rounded-none border-2 border-white/20 text-white font-bold hover:bg-white hover:text-black transition-all duration-300 px-8 h-12"
                data-testid="hero-secondary-cta-button"
              >
                See How It Works
              </Button>
            </div>
            <div className="mt-10 flex items-center justify-center gap-6 text-white/40 text-sm">
              <span className="flex items-center gap-1.5"><GoogleLogo className="h-4 w-4" /> Google Ads</span>
              <span className="flex items-center gap-1.5"><FacebookLogo className="h-4 w-4" /> Meta Ads</span>
              <span className="hidden sm:inline">Integrated directly with Google Ads &amp; Meta Ads APIs</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* AGITATION BENTO */}
      <section className="py-24 sm:py-32 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 max-w-2xl">
            <SectionOverline>The Problem</SectionOverline>
            <h2 className="font-landing-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4">
              The Solo-Agency Trap
            </h2>
            <p className="mt-4 text-white/60 text-base leading-relaxed">
              You didn't start an agency to be a glorified spreadsheet updater.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {AGITATION_ITEMS.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="bg-[#0A0D1C] border border-white/10 hover:border-white/30 transition-colors duration-300 p-8 sm:p-10"
                data-testid={`agitation-card-${i}`}
              >
                <item.icon className="h-9 w-9 mb-6" style={{ color: YELLOW }} weight="bold" />
                <h3 className="font-landing-heading font-semibold text-xl mb-3">{item.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MECHANISM */}
      <section id="mechanism-section" className="py-24 sm:py-32 px-6 border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 max-w-2xl">
            <SectionOverline>The Solution</SectionOverline>
            <h2 className="font-landing-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4">
              Meet Your Autonomous Co-Pilot
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {MECHANISM_ITEMS.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="relative p-8 sm:p-10 border border-white/10 bg-[#0A0D1C]"
                data-testid={`mechanism-card-${i}`}
              >
                <span className="absolute top-6 right-8 font-landing-heading font-black text-5xl text-white/[0.06]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <item.icon className="h-9 w-9 mb-6" style={{ color: YELLOW }} weight="bold" />
                <h3 className="font-landing-heading font-semibold text-xl mb-3">{item.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 sm:py-32 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <SectionOverline>Proof</SectionOverline>
            <h2 className="font-landing-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4">
              Trusted by solo operators<br className="hidden sm:block" /> scaling beyond their limits.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.author}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="bg-[#0A0D1C] border border-white/10 p-8 flex flex-col"
                data-testid={`testimonial-card-${i}`}
              >
                <Quotes className="h-8 w-8 mb-4" style={{ color: YELLOW }} weight="fill" />
                <p className="text-white/80 text-sm leading-relaxed mb-6 flex-1">"{t.quote}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                  <img src={t.img} alt={t.author} className="h-10 w-10 object-cover" />
                  <div>
                    <p className="text-sm font-semibold">{t.author}</p>
                    <p className="text-xs text-white/40">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-24 sm:py-32 px-6 border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <SectionOverline>Pricing</SectionOverline>
            <h2 className="font-landing-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4">
              Simple, ROI-Positive Pricing
            </h2>
            <p className="mt-4 text-white/60 text-base">No complex tiering. Pay for what you use. Cancel anytime.</p>
          </div>

          <div className="flex items-center justify-center gap-1 border border-white/15 p-1 w-fit mx-auto mb-12" data-testid="landing-billing-cycle-toggle">
            <button
              type="button"
              onClick={() => setBillingCycle("monthly")}
              className={`px-5 py-2 text-sm font-bold uppercase tracking-wide transition-colors ${billingCycle === "monthly" ? "text-black" : "text-white/50 hover:text-white"}`}
              style={billingCycle === "monthly" ? { backgroundColor: YELLOW } : {}}
              data-testid="landing-billing-monthly-button"
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle("annual")}
              className={`px-5 py-2 text-sm font-bold uppercase tracking-wide transition-colors flex items-center gap-2 ${billingCycle === "annual" ? "text-black" : "text-white/50 hover:text-white"}`}
              style={billingCycle === "annual" ? { backgroundColor: YELLOW } : {}}
              data-testid="landing-billing-annual-button"
            >
              Annual
              <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500 text-black font-black">SAVE 15%</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
            {Object.entries(tiers).map(([key, tier]) => (
              <div
                key={key}
                className={`relative p-8 sm:p-10 border bg-[#0A0D1C] ${key === "agency" ? "border-2" : "border-white/10"}`}
                style={key === "agency" ? { borderColor: YELLOW } : {}}
                data-testid={`landing-pricing-card-${key}`}
              >
                {key === "agency" && (
                  <span
                    className="absolute -top-3 left-8 px-2 py-1 text-[10px] font-black uppercase text-black"
                    style={{ backgroundColor: YELLOW }}
                  >
                    Most Popular
                  </span>
                )}
                <h3 className="font-landing-heading font-bold text-2xl capitalize">{tier.name}</h3>
                <div className="my-6">
                  <span className="font-landing-heading font-black text-4xl tracking-tight">
                    ${billingCycle === "annual" ? tier.annual_monthly_equivalent : tier.price}
                  </span>
                  <span className="text-white/50 text-sm"> /mo + {tier.ad_spend_pct}% of ad spend</span>
                  {billingCycle === "annual" && (
                    <p className="text-xs text-white/40 mt-1" data-testid={`landing-annual-subtext-${key}`}>
                      Billed ${tier.annual_price} per year
                    </p>
                  )}
                </div>
                <ul className="space-y-3 mb-8">
                  {TIER_FEATURES[key]?.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-white/70">
                      <Check className="h-4 w-4 shrink-0 mt-0.5" style={{ color: YELLOW }} weight="bold" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/login">
                  <Button
                    className={`w-full rounded-none font-bold uppercase tracking-wide h-12 transition-all duration-300 ${key === "agency" ? "text-black hover:-translate-y-0.5" : "bg-transparent border-2 border-white/20 text-white hover:bg-white hover:text-black"}`}
                    style={key === "agency" ? { backgroundColor: YELLOW } : {}}
                    data-testid={`landing-pricing-cta-${key}`}
                  >
                    Choose {tier.name}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 sm:py-32 px-6 border-t border-white/10">
        <div className="max-w-3xl mx-auto">
          <div className="mb-12">
            <SectionOverline>Objections</SectionOverline>
            <h2 className="font-landing-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4">
              Destroying Your Objections
            </h2>
          </div>
          <div className="space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <FaqRow key={item.q} item={item} index={i} openIndex={openFaq} setOpenIndex={setOpenFaq} />
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 sm:py-32 px-6 border-t border-white/10 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{ background: `radial-gradient(circle at 50% 50%, ${YELLOW}, transparent 60%)` }}
        />
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <ShieldCheck className="h-10 w-10 mx-auto mb-6" style={{ color: YELLOW }} weight="bold" />
          <h2 className="font-landing-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
            Stop working IN the ads manager.<br />Start working ON your agency.
          </h2>
          <div className="mt-10">
            <Link to="/login">
              <Button
                size="lg"
                className="gap-2 rounded-none font-bold uppercase tracking-wide text-black px-10 h-14 text-base hover:-translate-y-1 transition-all duration-300"
                style={{ backgroundColor: YELLOW }}
                data-testid="final-cta-button"
              >
                Claim Your 14-Day Free Trial <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
          <p className="mt-5 text-sm text-white/40">No credit card required for setup. Cancel anytime.</p>
        </div>
      </section>

      <footer className="py-10 px-6 border-t border-white/10 text-center text-sm text-white/30">
        © {new Date().getFullYear()} AdCrew OS. Built for solopreneurs and one-person agencies.
      </footer>
    </div>
  );
}
